<template>
    <div id="report-query-container">
        <!-- 主要内容区域 -->
        <el-main>
            <div class="query-container">
                <!-- 页面标题和说明 -->
                <div class="page-header">
                    <h1 class="page-title">专项测试成绩报告查询</h1>
                    <p class="page-description">
                        请输入您的用户识别信息，查询您的专项测试成绩报告。所有信息将严格保密，请放心使用。
                    </p>
                </div>

                <!-- 查询表单 -->
                <div class="query-form">
                    <el-card class="form-card" shadow="hover">
                        <div slot="header" class="card-header">
                            <i class="el-icon-user"></i>
                            <span>用户识别号</span>
                        </div>

                        <el-form :model="queryForm" :rules="rules" ref="queryForm" label-width="100px">
                            <el-form-item label="用户识别号" prop="userIdentifier">
                                <el-input
                                    v-model="queryForm.userIdentifier"
                                    placeholder="请输入您的用户识别号"
                                    prefix-icon="el-icon-user"
                                    size="large"
                                    clearable
                                    @keyup.enter.native="handleQuery"
                                    @focus="handleInputFocus"
                                    @blur="handleInputBlur"
                                    :class="{ 'input-focused': inputFocused }"
                                    autocomplete="off"
                                    autocapitalize="off"
                                    autocorrect="off"
                                    spellcheck="false"
                                    inputmode="text"
                                >
                                </el-input>
                            </el-form-item>

                            <!-- 验证码区域 -->
                            <el-form-item label="安全验证" prop="captcha">
                                <div class="captcha-container">
                                    <el-button
                                        id="captcha-btn"
                                        type="primary"
                                        plain
                                        size="large"
                                        @click="showCaptcha"
                                        :disabled="captchaVerified"
                                        class="captcha-btn"
                                    >
                                        <i class="el-icon-shield" style="margin-right: 8px;"></i>
                                        {{ captchaVerified ? '验证通过' : '点击进行安全验证' }}
                                    </el-button>
                                    <div v-if="captchaVerified" class="captcha-success">
                                        <i class="el-icon-success" style="color: #67C23A; margin-right: 4px;"></i>
                                        <span style="color: #67C23A; font-size: 14px;">安全验证已通过</span>
                                    </div>
                                </div>
                            </el-form-item>

                            <el-form-item>
                                <el-button
                                    type="primary"
                                    size="large"
                                    class="query-btn"
                                    @click="handleQuery"
                                    :loading="loading"
                                >
                                    <i class="el-icon-search"></i>
                                    查询成绩报告
                                </el-button>
                            </el-form-item>
                        </el-form>
                    </el-card>
                </div>

                <!-- 查询帮助 -->
                <div class="help-section">
                    <el-card class="help-card" shadow="never">
                        <div slot="header" class="card-header">
                            <i class="el-icon-question"></i>
                            <span>查询帮助</span>
                        </div>

                        <div class="help-content">
                            <div class="help-item">
                                <i class="el-icon-info"></i>
                                <span>用户识别号不正确时，将无法查询到成绩报告</span>
                            </div>
                            <div class="help-item">
                                <i class="el-icon-warning"></i>
                                <span>如遇到查询问题，请联系客服或查看常见问题解答</span>
                            </div>
                            <div class="help-item">
                                <i class="el-icon-lock"></i>
                                <span>您的个人信息受到严格保护，查询过程安全可靠</span>
                            </div>
                        </div>
                    </el-card>
                </div>

                <!-- 查询结果区域 -->
                <div class="result-section" v-if="showResult">
                    <el-card class="result-card" shadow="hover">
                        <div slot="header" class="card-header">
                            <i class="el-icon-document"></i>
                            <span>查询结果</span>
                        </div>

                        <div class="result-content" v-if="queryResult">
                            <el-alert
                                :title="queryResult.reports && queryResult.reports.length > 0 ? '查询成功' : '查询完成'"
                                :type="queryResult.reports && queryResult.reports.length > 0 ? 'success' : 'warning'"
                                :description="queryResult.reports && queryResult.reports.length > 0 ? '已找到您的成绩报告信息' : '暂未找到可用的成绩报告'"
                                show-icon
                                :closable="false">
                            </el-alert>

                            <div class="result-info">
                                <p><strong>用户识别号：</strong>{{ queryResult.userIdentifier }}</p>
                                <p><strong>查询时间：</strong>{{ queryResult.queryTime }}</p>
                                <p><strong>报告状态：</strong>{{ queryResult.status }}</p>
                                <p v-if="queryResult.message"><strong>查询信息：</strong>{{ queryResult.message }}</p>
                            </div>

                            <!-- 报告列表 -->
                            <div v-if="queryResult.reports && queryResult.reports.length > 0" class="reports-list">
                                <h4>可下载报告：</h4>
                                <div v-for="report in queryResult.reports" :key="report.id" class="report-item">
                                    <div class="report-info">
                                        <i class="el-icon-document"></i>
                                        <span class="report-name">{{ report.name }}</span>
                                    </div>
                                    <el-button
                                        type="primary"
                                        size="small"
                                        @click="downloadReport(report.url, report.name)"
                                        class="download-btn">
                                        <i class="el-icon-download"></i>
                                        下载
                                    </el-button>
                                </div>
                            </div>
                        </div>

                        <div class="no-result" v-else>
                            <el-alert
                                title="未找到相关记录"
                                type="warning"
                                description="请检查您输入的用户识别号是否正确，或联系客服获取帮助"
                                show-icon
                                :closable="false">
                            </el-alert>
                        </div>
                    </el-card>
                </div>
            </div>
        </el-main>
    </div>
</template>

<script>
import { queryReport } from '@/request/index';

export default {
    name: 'ReportQuery',
    data() {
        return {
            loading: false,
            showResult: false,
            inputFocused: false,
            isMobile: false,
            queryForm: {
                userIdentifier: '',
                captcha: ''
            },
            rules: {
                userIdentifier: [
                    { required: true, message: '请输入用户识别号', trigger: 'blur' },
                    { min: 6, message: '用户识别号长度不能少于6位', trigger: 'blur' }
                ],
                captcha: [
                    {
                        validator: (rule, value, callback) => {
                            if (!this.captchaVerified) {
                                callback(new Error('请完成安全验证'));
                            } else {
                                callback();
                            }
                        },
                        trigger: 'change'
                    }
                ]
            },
            queryResult: null,
            // 验证码相关
            captchaVerified: false,
            captchaTicket: '',
            captchaRandstr: '',
            captchaInstance: null
        }
    },
    methods: {
        handleQuery() {
            // 检查验证码是否通过
            if (!this.captchaVerified) {
                this.$message.error('请先完成安全验证');
                return;
            }

            this.$refs.queryForm.validate((valid) => {
                if (valid) {
                    this.loading = true;

                    // 移动端收起键盘
                    if (this.isMobile) {
                        document.activeElement.blur();
                    }

                    // 准备API请求参数
                    const params = {
                        Ticket: this.captchaTicket || "111",
                        Randstr: this.captchaRandstr || "111",
                        user_id: this.queryForm.userIdentifier
                    };
                    let data = {
                        params:params
                    }
                    // 调用查询API
                    queryReport(data).then(response => {
                        this.loading = false;
                        this.showResult = true;

                        console.log('查询响应:', response);

                        // 根据API响应处理结果
                        if (response && response.code) {
                            if(response.code != 200)
                            {
                                this.$message.error(response.message || '查询失败，请检查用户识别号是否正确');
                                // 查询失败时重置验证码，要求用户重新验证
                                this.resetCaptcha();
                                return false;
                            }
                            // 查询成功，处理返回的报告数据
                            const reportData = response || {};
                            let reports = [];

                            // 安全检查：确保 reportData 是一个对象
                            if (reportData && typeof reportData === 'object') {
                                // 提取报告信息（数据中的数字键对应不同的报告）
                                Object.keys(reportData).forEach(key => {
                                    if (key !== 'message' && key !== 'code' && reportData[key] && typeof reportData[key] === 'object') {
                                        reports.push({
                                            id: key,
                                            name: reportData[key].name || '未知报告',
                                            url: reportData[key].url || ''
                                        });
                                    }
                                });
                            }

                            this.queryResult = {
                                userIdentifier: this.queryForm.userIdentifier,
                                queryTime: new Date().toLocaleString(),
                                status: reports.length > 0 ? '报告可下载' : '暂无可用报告',
                                reports: reports,
                                message: (response.data && response.data.message) || '查询成功'
                            };

                            this.$message.success('查询成功');

                            // 滚动到结果区域
                            this.$nextTick(() => {
                                const resultEl = document.querySelector('.result-section');
                                if (resultEl) {
                                    resultEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                            });
                            this.resetCaptcha();
                        } else {
                            // 查询失败或无结果
                            this.queryResult = null;
                            this.$message.error(response.message || '查询失败，请检查用户识别号是否正确');
                            // 查询失败时重置验证码，要求用户重新验证
                            this.resetCaptcha();
                        }
                    }).catch(error => {
                        this.loading = false;
                        this.showResult = true;
                        this.queryResult = null;

                        console.error('查询错误:', error);
                        this.$message.error('查询请求失败，请检查网络连接或稍后重试');
                        // 网络错误时重置验证码，要求用户重新验证
                        this.resetCaptcha();
                    });
                } else {
                    this.$message.error('请正确填写查询信息');
                    return false;
                }
            });
        },

        downloadReport(url, fileName) {
            if (!url) {
                this.$message.error('下载链接无效');
                return;
            }

            try {
                // 创建一个临时的 a 标签来触发下载
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName || '成绩报告.pdf';
                link.target = '_blank';

                // 添加到页面并触发点击
                document.body.appendChild(link);
                link.click();

                // 清理
                document.body.removeChild(link);

                this.$message.success('开始下载报告');
            } catch (error) {
                console.error('下载失败:', error);
                this.$message.error('下载失败，请稍后重试');
            }
        },

        // 获取用户IP地址
        getUserIP() {
            // 在实际应用中，通常由后端获取真实IP
            // 这里返回一个默认值，或者可以通过第三方服务获取
            return '127.0.0.1';
        },

        handleInputFocus() {
            this.inputFocused = true;

            // 移动端输入框获取焦点时，滚动到可见区域
            if (this.isMobile) {
                setTimeout(() => {
                    const activeElement = document.activeElement;
                    if (activeElement) {
                        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 300);
            }
        },

        handleInputBlur() {
            this.inputFocused = false;
        },

        checkMobile() {
            // 检测是否为移动设备
            this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                           (window.matchMedia && window.matchMedia('(max-width: 767px)').matches);

            // 添加窗口大小变化监听
            window.addEventListener('resize', this.handleResize);
        },

        handleResize() {
             this.isMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
         },

         // 显示验证码
         showCaptcha() {
             console.log('开始显示验证码...');
             console.log('TencentCaptcha 是否可用:', typeof TencentCaptcha !== 'undefined');

             if (typeof TencentCaptcha === 'undefined') {
                 console.error('TencentCaptcha 未定义，可能的原因：');
                 console.error('1. JS库未正确加载');
                 console.error('2. 网络连接问题');
                 console.error('3. 腾讯云服务异常');
                 this.$message.error('验证码组件加载失败，请检查网络连接后刷新页面重试');
                 // 调用容灾处理
                 this.loadErrorCallback();
                 return;
             }

             try {
                 console.log('创建验证码实例，AppId:', '198291245');

                 // 按照官方文档格式创建验证码实例
                 // new TencentCaptcha(appId, callback, options)
                 this.captchaInstance = new TencentCaptcha('198291245', this.captchaCallback, {
                     // 用户语言设置
                     userLanguage: 'zh-cn',
                     // 验证码场景ID
                     bizState: 'report_query',
                     // 验证码渲染完成回调
                     showFn: (ret) => {
                         console.log('验证码渲染完成:', ret);
                         const { duration, sid } = ret;
                         console.log('渲染耗时:', duration + 'ms', '链路sid:', sid);
                     },
                     // 验证码关闭回调
                     close: this.captchaClose,
                     // 验证码错误回调
                     error: this.captchaError
                 });

                 console.log('验证码实例创建成功，准备显示...');

                 // 显示验证码
                 this.captchaInstance.show();

                 console.log('验证码显示命令已执行');

             } catch (error) {
                 console.error('创建或显示验证码时发生错误:', error);
                 this.$message.error('验证码初始化失败: ' + error.message);
                 // 调用容灾处理
                 this.loadErrorCallback();
             }
         },

         // 验证码验证成功回调
         captchaCallback(res) {
             console.log('验证码回调结果:', res);
             console.log('返回参数详情:');
             console.log('- ret (验证结果):', res.ret);
             console.log('- ticket (验证票据):', res.ticket);
             console.log('- randstr (随机串):', res.randstr);
             console.log('- CaptchaAppId (应用ID):', res.CaptchaAppId);
             console.log('- bizState (业务状态):', res.bizState);
             console.log('- verifyDuration (验证耗时):', res.verifyDuration + 'ms');
             console.log('- actionDuration (操作耗时):', res.actionDuration + 'ms');
             console.log('- sid (链路ID):', res.sid);

             if (res.errorCode) {
                 console.log('- errorCode (错误码):', res.errorCode);
                 console.log('- errorMessage (错误信息):', res.errorMessage);
             }

             if (res.ret === 0) {
                 // 验证成功
                 console.log('✅ 验证码验证成功');

                 this.captchaVerified = true;
                 this.captchaTicket = res.ticket;
                 this.captchaRandstr = res.randstr;

                 // 设置表单验证字段值
                 this.queryForm.captcha = 'verified';

                 // 触发表单验证
                 this.$refs.queryForm.validateField('captcha');

                 // 根据是否有错误码显示不同消息
                 if (res.errorCode) {
                     this.$message.success('安全验证通过（容灾模式）');
                 } else {
                     this.$message.success('安全验证通过');
                 }
             } else if (res.ret === 2) {
                 // 用户主动关闭验证码
                 console.log('ℹ️ 用户主动关闭验证码');
                 this.$message.info('验证已取消');
             } else {
                 // 验证失败
                 console.error('❌ 验证码验证失败，返回码:', res.ret);
                 this.$message.error('验证失败，请重试');
             }
         },

         // 验证码关闭回调
         captchaClose() {
             console.log('验证码窗口已关闭');
         },

         // 验证码错误回调
         captchaError(err) {
             console.error('验证码错误详情:', err);
             console.error('错误类型:', typeof err);
             console.error('错误内容:', JSON.stringify(err, null, 2));

             let errorMessage = '验证码加载失败';
             if (err && err.message) {
                 errorMessage += ': ' + err.message;
             } else if (typeof err === 'string') {
                 errorMessage += ': ' + err;
             }

             this.$message.error(errorMessage + '，请重试');
         },

         // 重置验证码状态
         resetCaptcha() {
             this.captchaVerified = false;
             this.captchaTicket = '';
             this.captchaRandstr = '';
             this.queryForm.captcha = '';
             if (this.captchaInstance) {
                 this.captchaInstance.destroy();
                 this.captchaInstance = null;
             }
         },

         // 验证码加载错误处理函数（容灾机制）
         loadErrorCallback() {
             console.log('执行验证码容灾处理...');
             const appid = '198291245';
             // 生成容灾票据
             const ticket = 'trerror_1001_' + appid + '_' + Math.floor(new Date().getTime() / 1000);
             const randstr = '@' + Math.random().toString(36).substr(2);

             console.log('生成容灾票据:', { ticket, randstr });

             // 调用回调函数，模拟验证成功
             this.captchaCallback({
                 ret: 0,
                 randstr: randstr,
                 ticket: ticket,
                 errorCode: 1001,
                 errorMessage: 'jsload_error'
             });
         },

         // 检查验证码库是否正确加载
         checkCaptchaLibrary() {
             console.log('检查验证码库加载状态...');

             // 延迟检查，确保外部脚本有时间加载
             setTimeout(() => {
                 if (typeof TencentCaptcha !== 'undefined') {
                     console.log('✅ 腾讯云验证码库加载成功');
                     console.log('TencentCaptcha 版本:', TencentCaptcha.version || '未知');
                     console.log('TencentCaptcha 方法:', Object.getOwnPropertyNames(TencentCaptcha.prototype || {}));
                 } else {
                     console.error('❌ 腾讯云验证码库未加载');
                     console.error('可能的解决方案：');
                     console.error('1. 检查网络连接');
                     console.error('2. 确认腾讯云服务可用');
                     console.error('3. 检查防火墙或广告拦截器设置');

                     this.$message.warning('验证码服务加载异常，将使用容灾模式');
                 }
             }, 2000);
         }
     },

     mounted() {
         // 设置页面标题
         document.title = '专项测试成绩报告查询';
         // 初始化移动端检测
         this.checkMobile();
         // 检查验证码JS库是否加载
         this.checkCaptchaLibrary();
     },

     beforeDestroy() {
         window.removeEventListener('resize', this.handleResize);
     }
}
</script>

<style scoped lang="scss">
#report-query-container {
    /*min-height: 100vh;*/
    background: #f8f9fa;
}

.header-section {
    background: white;
    padding: 20px 0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);

    .box {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
    }
}

.el-main {
    padding: 40px 20px;
}

.query-container {
    max-width: 800px;
    margin: 0 auto;
}

.page-header {
    text-align: center;
    margin-bottom: 40px;

    .page-title {
        font-size: 32px;
        color: var(--color);
        margin-bottom: 16px;
        font-weight: 600;
    }

    .page-description {
        font-size: 16px;
        color: var(--text_color_2);
        line-height: 1.6;
        margin: 0;
    }
}

.query-form {
    margin-bottom: 40px;

    .form-card {
        border-radius: 12px;
        border: none;
    }

    // 表单标签样式
    :deep(.el-form-item__label) {
        font-weight: 500;
        color: var(--text_color_1);
        font-size: 14px;
        line-height: 3.5;
        text-align: right;
        padding-right: 12px;
    }

    .card-header {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 600;
        color: var(--color);

        i {
            margin-right: 8px;
            font-size: 20px;
        }
    }

    .el-input {
        margin-bottom: 20px;

        ::v-deep .el-input__inner {
            height: 50px;
            line-height: 50px;
            border-radius: 8px;
            border: 2px solid #e4e7ed;
            transition: all 0.3s;

            &:focus {
                border-color: var(--color);
                box-shadow: 0 0 0 2px rgba(0, 66, 37, 0.1);
            }
        }
    }

    // 验证码样式
    .captcha-container {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .captcha-btn {
            width: 100%;
            height: 50px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 500;
            transition: all 0.3s;

            &:disabled {
                background-color: #f0f9ff;
                border-color: #67C23A;
                color: #67C23A;
                cursor: not-allowed;

                &:hover {
                    background-color: #f0f9ff;
                    border-color: #67C23A;
                    color: #67C23A;
                }
            }

            &:not(:disabled):hover {
                background-color: #409EFF;
                border-color: #409EFF;
                color: white;
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 66, 37, 0.3);
            }
        }

        .captcha-success {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px 16px;
            background-color: #f0f9ff;
            border: 1px solid #67C23A;
            border-radius: 6px;
            animation: fadeInUp 0.3s ease-out;
        }
    }

    .query-btn {
        width: 100%;
        height: 50px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 8px;
        color: white;
        background: var(--color);
        border-color: var(--color);

        &:hover {
            background: var(--color);
            border-color: var(--color);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 66, 37, 0.3);
        }

        i {
            margin-right: 8px;
        }
    }
}

.help-section {
    /*margin-bottom: 40px;*/

    .help-card {
        border-radius: 12px;
        border: 1px solid #e4e7ed;

        .card-header {
            display: flex;
            align-items: center;
            font-size: 16px;
            font-weight: 600;
            color: var(--text_color);

            i {
                margin-right: 8px;
                color: var(--color);
            }
        }
    }

    .help-content {
        .help-item {
            display: flex;
            align-items: center;
            margin-bottom: 12px;
            font-size: 14px;
            color: var(--text_color_2);

            &:last-child {
                margin-bottom: 0;
            }

            i {
                margin-right: 8px;
                color: #909399;
                flex-shrink: 0;
            }
        }
    }
}

.result-section {
    .result-card {
        border-radius: 12px;
        border: none;

        .card-header {
            display: flex;
            align-items: center;
            font-size: 18px;
            font-weight: 600;
            color: var(--color);

            i {
                margin-right: 8px;
                font-size: 20px;
            }
        }
    }

    .result-content {
        .result-info {
            margin: 20px 0;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;

            p {
                margin: 8px 0;
                font-size: 14px;
                color: var(--text_color);

                strong {
                    color: var(--color);
                }
            }
        }

        .download-btn {
            background: var(--color);
            border-color: var(--color);

            &:hover {
                background: var(--color);
                border-color: var(--color);
            }

            i {
                margin-right: 8px;
            }
        }

        .reports-list {
            margin-top: 20px;

            h4 {
                margin: 0 0 15px 0;
                font-size: 16px;
                color: var(--color);
                font-weight: 600;
            }

            .report-item {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 15px;
                margin-bottom: 10px;
                background: #f8f9fa;
                border-radius: 8px;
                border: 1px solid #e9ecef;
                transition: all 0.3s ease;

                &:hover {
                    background: #e9ecef;
                    border-color: var(--color);
                }

                &:last-child {
                    margin-bottom: 0;
                }

                .report-info {
                    display: flex;
                    align-items: center;
                    flex: 1;

                    i {
                        margin-right: 10px;
                        font-size: 18px;
                        color: var(--color);
                    }

                    .report-name {
                        font-size: 14px;
                        color: var(--text_color);
                        font-weight: 500;
                    }
                }

                .download-btn {
                    margin-left: 15px;
                    padding: 8px 16px;
                    font-size: 12px;
                }
            }
        }
    }
}

/* 响应式设计 - 大屏幕 (1200px+) */
@media (min-width: 1200px) {
    .query-container {
        max-width: 1000px;
    }

    .page-header {
        margin-bottom: 60px;
        position: relative;
    }

    .page-header::after {
        content: '';
        position: absolute;
        bottom: -20px;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 3px;
        background: linear-gradient(90deg, var(--color) 0%, var(--color_2) 100%);
        border-radius: 2px;
    }

    .page-header .page-title {
        font-size: 40px;
        margin-bottom: 24px;
        font-weight: 700;
        background: linear-gradient(90deg, var(--color) 0%, var(--color_2) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }

    .page-header .page-description {
        font-size: 18px;
        margin: 0 auto;
        line-height: 1.8;
        color: #606266;
    }

    .query-form {
        margin-bottom: 60px;
    }

    .query-form .form-card {
        padding: 50px;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    }

    .query-form .form-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
    }

    .query-form .card-header h3 {
         font-size: 20px;
         font-weight: 600;
         color: #303133;
         margin-bottom: 8px;
     }

     .query-form .el-input .el-input__inner {
         height: 52px;
         line-height: 52px;
         font-size: 16px;
         padding: 0 20px;
         border-radius: 12px;
         border: 2px solid #E4E7ED;
         transition: all 0.3s ease;
     }

     .query-form .el-input .el-input__inner:hover {
         border-color: #C0C4CC;
         box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
     }

     .query-form .el-input .el-input__inner:focus {
         border-color: #409EFF;
         box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
     }

    .help-section {
        margin-bottom: 60px;
    }

    .help-section .help-card {
        border-radius: 12px;
        transition: all 0.3s ease;
    }

    .help-section .help-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    }

    .result-section .result-card {
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
    }

    .result-section .download-btn {
        border-radius: 8px;
        transition: all 0.3s ease;
    }

    .result-section .download-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(103, 194, 58, 0.3);
    }

    /* PC端双列布局 */
    .content-layout {
        display: grid;
        grid-template-columns: 1fr 400px;
        gap: 40px;
        align-items: start;
    }

    .content-layout .main-content {
        grid-column: 1;
    }

    .content-layout .sidebar-content {
        grid-column: 2;
    }
}

/* 超大屏幕优化 (1440px+) */
@media screen and (min-width: 1440px) {
    .query-container {
        max-width: 1200px;
        padding: 60px 40px;
    }

    .page-header .page-title {
        font-size: 48px;
        margin-bottom: 28px;
    }

    .page-header .page-description {
        font-size: 20px;
    }

    .query-form .form-card {
        padding: 60px;
        border-radius: 20px;
    }

    .query-form .el-input .el-input__inner {
        height: 56px;
        line-height: 56px;
        font-size: 18px;
        padding: 0 24px;
    }

    .help-section .el-card,
    .result-section .el-card {
        padding: 30px;
    }
}

/* 中等屏幕优化 (992px - 1199px) */
@media screen and (min-width: 992px) and (max-width: 1199px) {
    .query-container {
        max-width: 900px;
        padding: 40px 20px;
    }

    .page-header .page-title {
        font-size: 36px;
    }

    .page-header .page-description {
        font-size: 17px;
    }

    .query-form .form-card {
        padding: 40px;
    }

    .query-form .el-input .el-input__inner {
        height: 50px;
        line-height: 50px;
        font-size: 16px;
    }
}

// 平板设备 (768px - 1199px)
@media (min-width: 768px) and (max-width: 1199px) {
    .el-main {
        padding: 30px 15px;
    }

    .query-container {
        max-width: 700px;
        padding: 0 15px;
    }

    .page-header {
        margin-bottom: 40px;

        .page-title {
            font-size: 28px;
        }

        .page-description {
            font-size: 16px;
        }
    }

    .query-form {
        .form-card {
            padding: 30px;
        }

        .el-input ::v-deep .el-input__inner {
            height: 55px;
            line-height: 55px;
            font-size: 15px;
        }
    }

    .help-content {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;

        .help-item {
            margin-bottom: 0;
        }
    }
}

// 移动设备 (最大 767px)
@media (max-width: 767px) {
    #report-query-container {
        background: #f8f9fa;
    }

    .header-section {
        padding: 15px 0;

        .box {
            padding: 0 15px;
        }

        .el-breadcrumb {
            font-size: 14px;
        }
    }

    .el-main {
        padding: 20px 10px;
    }

    .query-container {
        padding: 0 5px;
        max-width: 100%;
    }

    .page-header {
        margin-bottom: 30px;
        padding: 0 10px;

        .page-title {
            font-size: 22px;
            margin-bottom: 12px;
            line-height: 1.3;
        }

        .page-description {
            font-size: 14px;
            line-height: 1.5;
        }
    }

    .query-form {
        margin-bottom: 30px;

        .form-card {
            margin: 0 5px;
            border-radius: 8px;

            ::v-deep .el-card__header {
                padding: 15px 20px;

                .card-header {
                    font-size: 16px;
                }
            }

            ::v-deep .el-card__body {
                padding: 20px;
            }
        }

        .el-input {
            margin-bottom: 15px;

            ::v-deep .el-input__inner {
                height: 48px;
                line-height: 48px;
                font-size: 16px;
                border-radius: 6px;
                padding: 0 15px;
            }

            ::v-deep .el-input__prefix {
                left: 15px;
            }
        }

    }

    .help-section {

        .help-card {
            margin: 0 5px;
            border-radius: 8px;

            ::v-deep .el-card__header {
                padding: 15px 20px;

                .card-header {
                    font-size: 15px;
                }
            }

            ::v-deep .el-card__body {
                padding: 20px;
            }
        }

        .help-content {
            .help-item {
                margin-bottom: 15px;
                font-size: 13px;
                line-height: 1.4;

                i {
                    margin-right: 10px;
                    font-size: 14px;
                }
            }
        }
    }

    .result-section {
        .result-card {
            margin: 0 5px;
            border-radius: 8px;

            ::v-deep .el-card__header {
                padding: 15px 20px;
            }

            ::v-deep .el-card__body {
                padding: 20px;
            }
        }

        .result-content {
            .result-info {
                padding: 15px;
                margin: 15px 0;
                border-radius: 6px;

                p {
                    font-size: 13px;
                    margin: 6px 0;
                    line-height: 1.4;
                }
            }

            .download-btn {
                width: 100%;
                height: 48px;
                font-size: 16px;
                border-radius: 6px;
            }
        }

        .no-result {
            ::v-deep .el-alert {
                border-radius: 6px;

                .el-alert__title {
                    font-size: 14px;
                }

                .el-alert__description {
                    font-size: 13px;
                    line-height: 1.4;
                }
            }
        }
    }
}

// 超小屏幕 (最大 480px)
@media (max-width: 480px) {
    .header-section .box {
        padding: 0 10px;
    }

    .el-main {
        padding: 20px 5px;
    }

    .page-header {
        padding: 0 5px;

        .page-title {
            font-size: 20px;
            margin-bottom: 10px;
        }

        .page-description {
            font-size: 13px;
        }
    }

    .query-form .form-card,
    .help-section .help-card,
    .result-section .result-card {
        margin: 0;
        border-radius: 6px;

        ::v-deep .el-card__header {
            padding: 12px 15px;
        }

        ::v-deep .el-card__body {
            padding: 15px;
        }
    }

    .query-form {
        .el-input ::v-deep .el-input__inner {
            height: 44px;
            line-height: 44px;
            font-size: 15px;
            padding: 0 12px;
        }

    }

    .help-content .help-item {
        font-size: 12px;
        margin-bottom: 12px;

        i {
            margin-right: 8px;
            font-size: 13px;
        }
    }
}

// 横屏手机优化
@media (max-height: 500px) and (orientation: landscape) {
    .page-header {
        margin-bottom: 20px;

        .page-title {
            font-size: 18px;
            margin-bottom: 8px;
        }

        .page-description {
            font-size: 12px;
        }
    }

    .query-form,
    .help-section {
        margin-bottom: 20px;
    }

    .el-main {
        padding: 10px;
    }
}

/* 输入框焦点状态 */
.input-focused .el-input__inner {
    border-color: #409EFF !important;
    box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2) !important;
}

/* 移动端键盘适配 */
@media screen and (max-width: 767px) {
    .query-container {
        min-height: calc(100vh - 120px);
        padding-bottom: 50px;
    }

    /* iOS Safari 键盘适配 */
    @supports (-webkit-touch-callout: none) {
        .query-container {
            min-height: calc(100vh - 120px - env(keyboard-inset-height, 0px));
        }
    }
}

// 触摸设备优化
@media (hover: none) and (pointer: coarse) {

    /* 卡片触摸反馈 */
    .el-card {
        transition: transform 0.1s ease;
    }

    .el-card:active {
        transform: translateY(1px);
    }

    /* 下载按钮触摸反馈 */
    .download-btn {
        transition: all 0.2s ease;
    }

    .download-btn:active {
        transform: scale(0.98);
    }

    .download-btn {
        // 增加触摸目标大小
        min-height: 48px;
    }

    .el-input ::v-deep .el-input__inner {
        // 移动端输入框优化
        -webkit-appearance: none;
        border-radius: 6px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: text;
        user-select: text;

        &:focus {
            transform: scale(1.02);
            transition: transform 0.2s;
        }
    }

    // 卡片点击效果
    .form-card,
    .help-card,
    .result-card {
        transition: transform 0.2s, box-shadow 0.2s;

        &:active {
            transform: translateY(1px);
        }
    }

    /* 禁用长按选择 */
    .page-title, .page-description, .card-header {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }
}

/* 高分辨率屏幕优化 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    .el-card {
        border: 0.5px solid #EBEEF5;
    }
}

/* 超宽屏优化 (1920px+) */
@media screen and (min-width: 1920px) {
    .query-container {
        max-width: 1400px;
        padding: 80px 60px;
    }

    .page-header .page-title {
        font-size: 52px;
    }

    .page-header .page-description {
        font-size: 22px;
    }

    .query-form .form-card {
        padding: 80px;
        border-radius: 24px;
    }
}

/* 折叠屏设备优化 */
@media screen and (min-width: 540px) and (max-width: 720px) and (orientation: portrait) {
    .query-container {
        padding: 20px 16px;
    }

    .page-title {
        font-size: 24px;
    }

    .form-card {
        padding: 24px;
        margin-bottom: 20px;
    }

    .el-input__inner {
        height: 44px;
        line-height: 44px;
    }
}

/* 横屏模式优化 */
@media screen and (max-height: 500px) and (orientation: landscape) {
    .query-container {
        padding: 10px 20px;
    }

    .page-header {
        margin-bottom: 20px;
    }

    .page-title {
        font-size: 20px;
        margin-bottom: 8px;
    }

    .page-description {
        font-size: 14px;
        margin-bottom: 16px;
    }

    .form-card {
        padding: 20px;
        margin-bottom: 16px;
    }

    .help-section {
        margin-bottom: 20px;
    }
}

/* 打印样式 */
@media print {
    .query-container {
        max-width: none;
        padding: 20px;
        background: white;
    }

    .page-header::after {
        display: none;
    }

    .page-title {
        color: #000 !important;
        background: none !important;
        -webkit-text-fill-color: initial !important;
    }

    .query-btn,
    .download-btn {
        display: none;
    }

    .el-card {
        border: 1px solid #ddd;
        box-shadow: none;
    }

    .help-section,
    .result-section {
        page-break-inside: avoid;
    }
}

/* 动画定义 */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
